// Globals
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getUserAuthData, getUsersList, toggleActiveUser } from '../../feature';
import { usersListSelector } from '../../feature/selectors';

// Components
import { StyledContentBlock, StyledPageTitle, StyledPageWrapper } from '../../components/Styled';
import { UserModal, UsersList } from '../../components';

// Models
import { IUserProfile } from '@/models/users.model';

// Hooks
import { useModal } from '@/hooks';

// Helpers
import { GetUsersListDTO } from '../../dtos';
import { authWithToken } from '@/utils';
import { changeUserQuery } from '../../feature/slice';

const UsersListContainer = () => {
  const { t } = useTranslation(['usersList']);
  const dispatch = useAppDispatch();
  const { users, userQuery } = useAppState(usersListSelector);
  const [currentUserDetails, setCurrentUserDetails] = useState<IUserProfile | null>(null);
  const { isOpened, openModal, closeModal } = useModal();

  const getUsersListHandler = (params?: GetUsersListDTO) => {
    const dto = new GetUsersListDTO({ page: 1 });
    dispatch(getUsersList(params ?? dto));
  };

  const handlePageChange = (page: number) => {
    const dto = new GetUsersListDTO({
      ...userQuery,
      page,
    });

    dispatch(
      changeUserQuery({
        ...userQuery,
        page,
      }),
    );

    getUsersListHandler(dto);
  };

  const handleToggleActiveStatus = async () => {
    try {
      if (currentUserDetails) await dispatch(toggleActiveUser(currentUserDetails.id ?? ''));
      getUsersListHandler();
      closeModal();
      setCurrentUserDetails(null);
    } catch {}
  };

  const changeUserStatus = (user: IUserProfile) => {
    setCurrentUserDetails(user);
    openModal();
  };

  const authAsUser = async (id: string) => {
    const { data } = await dispatch(getUserAuthData(id)).unwrap();
    authWithToken(data?.access_token);
  };

  useEffect(() => {
    handlePageChange(1);
    return () => {
      dispatch(changeUserQuery({}));
    };
  }, []);

  return (
    <StyledPageWrapper>
      <StyledPageTitle>{t('users_list_tabs_users')}</StyledPageTitle>
      <StyledContentBlock>
        <UsersList
          currentPage={users?.meta?.current_page ?? 1}
          onPageChange={handlePageChange}
          usersList={users?.data ?? []}
          total={users?.meta?.total ?? 0}
          authAsUser={authAsUser}
          setCurrentUserDetails={changeUserStatus}
        />
      </StyledContentBlock>
      <UserModal
        isOpened={isOpened}
        closeModal={closeModal}
        currentUserDetails={currentUserDetails as IUserProfile}
        handleToggleActiveStatus={handleToggleActiveStatus}
      />
    </StyledPageWrapper>
  );
};

export default UsersListContainer;
